import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Input, Upload, message, Tabs, Space, Row, Col, Empty } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  TrophyOutlined, 
  PlusOutlined, 
  UploadOutlined, 
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { bodyConfig } from '../config/body';
import axios from 'axios';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Body = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);

  const [showRecordForm, setShowRecordForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // 表单数据
  const [formData, setFormData] = useState({
    height: '',
    weight: '',
    photo: null
  });

  // 计算BMI
  const calculateBMI = (height, weight) => {
    if (!height || !weight) return null;
    const heightInMeters = height / 100;
    return (weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  // 获取BMI分类
  const getBMICategory = (bmi) => {
    if (!bmi) return null;
    for (const [key, category] of Object.entries(bodyConfig.bmiCategories)) {
      if (bmi >= category.min && bmi <= category.max) {
        return { ...category, key };
      }
    }
    return null;
  };

  // 获取建议
  const getAdvice = (bmiCategory) => {
    if (!bmiCategory) return null;
    
    switch (bmiCategory.key) {
      case 'underweight':
        return bodyConfig.weightGainAdvice;
      case 'overweight':
      case 'obese':
        return bodyConfig.weightLossAdvice;
      case 'normal':
        return bodyConfig.maintenanceAdvice;
      default:
        return null;
    }
  };

  // 获取最新记录
  const getLatestRecord = () => {
    if (records.length === 0) return null;
    // 后端按 created_at DESC 排序，所以第一条记录是最新的
    return records[0];
  };

  // 获取当前BMI信息
  const getCurrentBMI = () => {
    const latest = getLatestRecord();
    if (!latest) return null;
    
    const bmi = calculateBMI(latest.height, latest.weight);
    const category = getBMICategory(bmi);
    
    return {
      bmi,
      category,
      advice: getAdvice(category)
    };
  };

  // 获取记录数据用于图表
  const getChartData = () => {
    return records.map(record => ({
      date: new Date(record.created_at).toLocaleDateString('zh-CN'),
      height: record.height,
      weight: record.weight,
      bmi: calculateBMI(record.height, record.weight)
    }));
  };

  // 加载记录数据
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get(bodyConfig.apiEndpoints.records);
      if (response.data.success) {
        setRecords(response.data.records || []);
      } else {
        message.error(response.data.message || '获取记录失败');
      }
    } catch (error) {
      console.error('获取记录失败:', error);
      message.error('获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存记录
  const saveRecord = async () => {
    if (!formData.height || !formData.weight) {
      message.error('请填写身高和体重');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        height: formData.height,
        weight: formData.weight,
        record_type: '体重记录'
      };

      const response = await axios.post(bodyConfig.apiEndpoints.create, requestData);
      if (response.data.success) {
        message.success('记录保存成功');
        setShowRecordForm(false);
        setFormData({ height: '', weight: '', photo: null });
        fetchRecords();
      } else {
        message.error(response.data.message || '保存失败');
      }
    } catch (error) {
      console.error('保存记录失败:', error);
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理文件上传
  const handleFileUpload = (file) => {
    setFormData(prev => ({ ...prev, photo: file }));
    return false; // 阻止自动上传
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchRecords();
  }, []);

  const currentBMI = getCurrentBMI();
  const chartData = getChartData();

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <h1 className="mod-header-title">
            <TrophyOutlined style={{ marginRight: 8, color: bodyConfig.bgColor }} />
            {bodyConfig.name}
          </h1>
          <div className="mod-header-subtitle">
            记录身高体重，跟踪 BMI 变化趋势，获取科学的饮食与运动建议
          </div>
        </div>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowRecordForm(true)}
            style={{ backgroundColor: bodyConfig.bgColor, borderColor: bodyConfig.bgColor }}
          >
            记录
          </Button>
        </Space>
      </div>

      {/* 主要内容 */}
      <div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: '概览',
              children: (
                <div>
                  {/* BMI 统计卡片 */}
                  {currentBMI && (
                    <div className="mod-stats">
                      <div className="mod-stat-card">
                        <div className="mod-stat-icon">📏</div>
                        <div>
                          <div className="mod-stat-value" style={{ color: '#1890ff' }}>
                            {getLatestRecord()?.height}
                            <span style={{ fontSize: 14, color: '#8c8c8c', marginLeft: 4 }}>cm</span>
                          </div>
                          <div className="mod-stat-label">当前身高</div>
                        </div>
                      </div>
                      <div className="mod-stat-card">
                        <div className="mod-stat-icon">⚖️</div>
                        <div>
                          <div className="mod-stat-value" style={{ color: '#52c41a' }}>
                            {getLatestRecord()?.weight}
                            <span style={{ fontSize: 14, color: '#8c8c8c', marginLeft: 4 }}>kg</span>
                          </div>
                          <div className="mod-stat-label">当前体重</div>
                        </div>
                      </div>
                      <div className="mod-stat-card">
                        <div className="mod-stat-icon">📊</div>
                        <div>
                          <div className="mod-stat-value" style={{ color: currentBMI.category.color }}>
                            {currentBMI.bmi}
                          </div>
                          <div className="mod-stat-label">当前 BMI</div>
                        </div>
                      </div>
                      <div className="mod-stat-card">
                        <div className="mod-stat-icon">🏃</div>
                        <div>
                          <div className="mod-stat-value" style={{ color: currentBMI.category.color, fontSize: 20 }}>
                            {currentBMI.category.label}
                          </div>
                          <div className="mod-stat-label">体重状态</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 身高体重变化曲线 */}
                  {chartData.length > 0 && (
                    <Card title="身高体重变化趋势" style={{ 
                      marginBottom: '24px',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                      border: 'none'
                    }} bodyStyle={{ padding: '6px' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip />
                          <Legend />
                          <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="height" 
                            stroke="#8884d8" 
                            name="身高(cm)"
                          />
                          <Line 
                            yAxisId="right"
                            type="monotone" 
                            dataKey="weight" 
                            stroke="#82ca9d" 
                            name="体重(kg)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  )}

                  {/* BMI 变化曲线 */}
                  {chartData.length > 0 && (
                    <Card title="BMI变化趋势" style={{
                      marginBottom: '24px',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #f0f1f4'
                    }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="bmi" 
                            stroke="#ff7300" 
                            name="BMI"
                            strokeWidth={3}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  )}

                  {/* 空状态 */}
                  {!currentBMI && chartData.length === 0 && (
                    <Empty
                      description='暂无身材数据，点击右上角"记录"按钮开始记录'
                      style={{ padding: '60px 0' }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowRecordForm(true)}
                        style={{ backgroundColor: bodyConfig.bgColor, borderColor: bodyConfig.bgColor }}
                      >
                        记录
                      </Button>
                    </Empty>
                  )}

                  {/* 健康建议 */}
                  {currentBMI && currentBMI.advice && (
                    <Card title={currentBMI.advice.title} style={{
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #f0f1f4'
                    }}>
                      {currentBMI.advice.sections.map((section, index) => (
                        <div key={index} style={{ marginBottom: '20px' }}>
                          <Title level={4} style={{ color: currentBMI.category.color }}>
                            {section.title}
                          </Title>
                          <ul style={{ paddingLeft: '20px' }}>
                            {section.items.map((item, itemIndex) => (
                              <li key={itemIndex} style={{ marginBottom: '8px' }}>
                                <Text>{item}</Text>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </Card>
                  )}
                </div>
              )
            },
            {
              key: 'meals',
              label: '餐饮建议',
              children: (
                <Card title="健康餐饮搭配建议">
                  <Tabs defaultActiveKey="breakfast">
                    <TabPane tab="早餐" key="breakfast">
                      <div>
                        {bodyConfig.mealSuggestions.breakfast.map((meal, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Text>{meal}</Text>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                    <TabPane tab="午餐" key="lunch">
                      <div>
                        {bodyConfig.mealSuggestions.lunch.map((meal, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Text>{meal}</Text>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                    <TabPane tab="晚餐" key="dinner">
                      <div>
                        {bodyConfig.mealSuggestions.dinner.map((meal, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Text>{meal}</Text>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                    <TabPane tab="零食" key="snacks">
                      <div>
                        {bodyConfig.mealSuggestions.snacks.map((meal, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Text>{meal}</Text>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                  </Tabs>
                </Card>
              )
            },
            {
              key: 'exercise',
              label: '运动建议',
              children: (
                <Card title="科学运动方案">
                  <Tabs defaultActiveKey="cardio">
                    <TabPane tab="有氧运动" key="cardio">
                      <div>
                        {bodyConfig.exerciseMethods.cardio.map((exercise, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Title level={5}>{exercise.name}</Title>
                            <Space direction="vertical" size="small">
                              <Text>时长：{exercise.duration}</Text>
                              <Text>频率：{exercise.frequency}</Text>
                              <Text>效果：{exercise.benefits}</Text>
                              <Text type="secondary">提示：{exercise.tips}</Text>
                            </Space>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                    <TabPane tab="力量训练" key="strength">
                      <div>
                        {bodyConfig.exerciseMethods.strength.map((exercise, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Title level={5}>{exercise.name}</Title>
                            <Space direction="vertical" size="small">
                              <Text>组数：{exercise.sets}</Text>
                              <Text>次数：{exercise.reps}</Text>
                              <Text>频率：{exercise.frequency}</Text>
                              <Text>效果：{exercise.benefits}</Text>
                              <Text type="secondary">提示：{exercise.tips}</Text>
                            </Space>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                    <TabPane tab="柔韧性训练" key="flexibility">
                      <div>
                        {bodyConfig.exerciseMethods.flexibility.map((exercise, index) => (
                          <Card key={index} size="small" style={{ marginBottom: '12px' }}>
                            <Title level={5}>{exercise.name}</Title>
                            <Space direction="vertical" size="small">
                              <Text>时长：{exercise.duration}</Text>
                              <Text>频率：{exercise.frequency}</Text>
                              <Text>效果：{exercise.benefits}</Text>
                              <Text type="secondary">提示：{exercise.tips}</Text>
                            </Space>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                  </Tabs>
                </Card>
              )
            },
            {
              key: 'records',
              label: '历史记录',
              children: (
                <Card title="身材记录历史" style={{
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #f0f1f4'
                }}>
                  {records.length === 0 ? (
                    <Empty description='暂无记录，点击"记录"按钮开始记录' style={{ padding: '32px 0' }} />
                  ) : (
                    <Row gutter={[14, 14]}>
                      {records.map((record, index) => {
                        const bmi = calculateBMI(record.height, record.weight);
                        const category = getBMICategory(bmi);
                        return (
                          <Col xs={24} sm={12} lg={8} key={index}>
                            <div className="mod-card">
                              <div className="mod-card-head">
                                <div className="mod-card-icon" style={{ background: `${category?.color}1a`, color: category?.color }}>
                                  ⚖️
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="mod-card-title">
                                    {record.weight}kg · {record.height}cm
                                  </div>
                                  <div className="mod-card-sub">
                                    {new Date(record.created_at).toLocaleString('zh-CN')}
                                  </div>
                                </div>
                              </div>
                              <div className="mod-badges">
                                <span className="mod-badge mod-badge-primary">BMI {bmi}</span>
                                <span className="mod-badge" style={{ color: category?.color, background: `${category?.color}14` }}>
                                  {category?.label}
                                </span>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </Card>
              )
            }
          ]}
        />
      </div>

      {/* 记录表单弹窗 */}
      {showRecordForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <Card 
            title="记录身材数据" 
            style={{ width: '90%', maxWidth: '500px' }}
            extra={
              <Button 
                type="text" 
                onClick={() => setShowRecordForm(false)}
              >
                ✕
              </Button>
            }
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong>身高 (cm)</Text>
                <Input
                  type="number"
                  placeholder="请输入身高"
                  value={formData.height}
                  onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                  style={{ marginTop: '8px' }}
                />
              </div>
              
              <div>
                <Text strong>体重 (kg)</Text>
                <Input
                  type="number"
                  placeholder="请输入体重"
                  value={formData.weight}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div>
                <Text strong>身材照片</Text>
                <Upload
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  style={{ marginTop: '8px' }}
                >
                  <Button icon={<UploadOutlined />}>
                    选择照片
                  </Button>
                </Upload>
                {formData.photo && (
                  <Text style={{ marginLeft: '8px' }}>
                    已选择: {formData.photo.name}
                  </Text>
                )}
              </div>

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button onClick={() => setShowRecordForm(false)}>
                    取消
                  </Button>
                  <Button 
                    type="primary"
                    onClick={saveRecord}
                    loading={loading}
                    style={{ backgroundColor: bodyConfig.bgColor, borderColor: bodyConfig.bgColor }}
                  >
                    保存
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Body;
